"use client";
import { useEffect, useState } from "react";
import axiosInstance from "@/service/axiosInstance";
import Loader from "@/components/Loader/Loader";
import Image from "next/image";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { CommonConstant, ToastOptions } from "@/utils/enum";

interface SchoolRanking {
  schoolName: string;
  gold: number;
  silver: number;
  bronze: number;
  totalMedals: number;
}

const SchoolsRanking = ({ academicYear }: { academicYear: string }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [rankings, setRankings] = useState<SchoolRanking[]>([]);
  const [page, setPage] = useState(0); // Number of items to skip (for pagination)
  const [hasMore, setHasMore] = useState(true); // To track if more data is available
  const LIMIT = 50;

  const getData = async (pageNum: number, reset = false) => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get(
        `/leaderboard/all-schools?page=${pageNum}&limit=${LIMIT}&year=${academicYear}`,
      );

      const newSchools = resp.data || [];
      const filteredSchools = newSchools.filter(
        (school: any) =>
          school.schoolName !== CommonConstant.OTHERS &&
          school.schoolName !== CommonConstant.OTHERS_CAPITALIZED &&
          school.schoolName !== CommonConstant.OTHERS_LOWER,
      );

      if (filteredSchools.length < LIMIT) {
        setHasMore(false);
      }

      if (filteredSchools.length === 0 && pageNum > 0) {
        toast.info("No more data available.", ToastOptions);
        setHasMore(false);
      }

      setRankings((prev) =>
        reset ? filteredSchools : [...prev, ...filteredSchools],
      );

      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch data. Please try again.", ToastOptions);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (academicYear) {
      setRankings([]);
      setPage(0);
      setHasMore(true);
      getData(0, true);
    }
  }, [academicYear]);

  const handleShowMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      getData(nextPage, false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div
        className="container px-3 px-lg-5 my-2"
        style={{ maxWidth: "1800px", margin: "auto" }}
      >
        <h1 className="fw-bold text-center text-lg-start">
          Top Schools Ranking
        </h1>

        {rankings?.length > 0 ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: "1000px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#e697ab" }}>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      Rank
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      School Name
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Image
                          src="/gold.svg"
                          alt="gold"
                          width={20}
                          height={20}
                          style={{
                            marginRight: "0.5rem",
                          }}
                        />
                        Gold
                      </div>
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Image
                          src="/silver.svg"
                          alt="silver"
                          width={20}
                          height={20}
                          style={{
                            marginRight: "0.5rem",
                          }}
                        />
                        Silver
                      </div>
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Image
                          src="/bronze.svg"
                          alt="bronze"
                          width={20}
                          height={20}
                          style={{
                            marginRight: "0.5rem",
                          }}
                        />
                        Bronze
                      </div>
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      Total Medals
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: "#fff" }}>
                  {rankings.map((school, index) => (
                    <tr
                      key={`${school.schoolName}-${index}`}
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      <td style={{ padding: "1rem" }}>#{index + 1}</td>
                      <td style={{ padding: "1rem" }}>
                        {school.schoolName.toUpperCase()}
                      </td>
                      <td style={{ padding: "1rem" }}>{school.gold}</td>
                      <td style={{ padding: "1rem" }}>{school.silver}</td>
                      <td style={{ padding: "1rem" }}>{school.bronze}</td>
                      <td style={{ padding: "1rem" }}>{school.totalMedals}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={handleShowMore}
                    disabled={loading}
                    style={{
                      padding: "0.2rem 0.5rem",
                      backgroundColor: "#e697ab",
                      color: "#57221B",
                      border: "none",
                      borderRadius: "0.5rem",
                      cursor: "pointer",
                      boxShadow: "-4px 4px 6px #CF6557",
                      float: "right",
                      margin: "1rem",
                    }}
                  >
                    {loading ? "Loading..." : "Show More"}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          !loading && (
            <p
              style={{
                color: "#6b7280",
                textAlign: "center",
                marginTop: "1rem",
              }}
            >
              Results Not Declared Yet
            </p>
          )
        )}
      </div>
    </>
  );
};

export default SchoolsRanking;
